import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_AR = [
  'يناير','فبراير','مارس','أبريل','مايو','يونيو',
  'يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر',
];

const fmt = (n) => (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ── Shared stat card ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = 'text-foreground', language }) => (
  <Card className="p-5 border border-border">
    <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{label}</p>
    <p className={`text-2xl font-bold font-mono-numeric mt-1 ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1" lang={language === 'ar' ? 'ar' : 'en'}>{sub}</p>}
  </Card>
);

// ── Table wrapper ─────────────────────────────────────────────────────────────
const ReportTable = ({ head, rows, foot }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-border bg-muted/30">
          {head.map((h, i) => (
            <th key={i} className={`px-3 py-2 font-medium text-muted-foreground ${i === 0 ? 'text-start' : 'text-end'}`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
            {row.map((cell, j) => (
              <td key={j} className={`px-3 py-2 font-mono-numeric ${j === 0 ? 'text-start' : 'text-end'}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
      {foot && (
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/30 font-semibold">
            {foot.map((cell, i) => (
              <td key={i} className={`px-3 py-2 font-mono-numeric ${i === 0 ? 'text-start' : 'text-end'}`}>{cell}</td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

// ── Section heading ───────────────────────────────────────────────────────────
const SectionTitle = ({ children, language }) => (
  <h3 className="text-base font-semibold mb-3 mt-6 first:mt-0" lang={language === 'ar' ? 'ar' : 'en'}>{children}</h3>
);

// ── Year filter ───────────────────────────────────────────────────────────────
const YearFilter = ({ value, onChange, t }) => {
  const years = [2024, 2025, 2026];
  return (
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="border border-border rounded-md px-3 py-1.5 text-sm bg-background"
    >
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
};

// ── Month + Year filter ───────────────────────────────────────────────────────
const MonthYearFilter = ({ month, year, onMonth, onYear, language }) => {
  const months = language === 'ar' ? MONTHS_AR : MONTHS;
  return (
    <div className="flex gap-2">
      <select value={month} onChange={e => onMonth(Number(e.target.value))}
        className="border border-border rounded-md px-3 py-1.5 text-sm bg-background">
        {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
      </select>
      <select value={year} onChange={e => onYear(Number(e.target.value))}
        className="border border-border rounded-md px-3 py-1.5 text-sm bg-background">
        {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 1 — REVENUE
// ══════════════════════════════════════════════════════════════════════════════
const RevenueReport = ({ invoices, payments, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');
  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  const clientPayments = useMemo(() =>
    payments.filter(p => p.entity_type === 'client'), [payments]);

  const paymentsByClient = useMemo(() => {
    const m = {};
    clientPayments.forEach(p => {
      m[p.entity_id] = (m[p.entity_id] || 0) + (p.amount || 0);
    });
    return m;
  }, [clientPayments]);

  const filteredInvoices = useMemo(() =>
    invoices.filter(inv => {
      const d = inv.date ? new Date(inv.date) : null;
      return d && d.getFullYear() === year;
    }), [invoices, year]);

  // By month
  const byMonth = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      const mo = new Date(inv.date).getMonth(); // 0-based
      if (!map[mo]) map[mo] = { invoiced: 0 };
      map[mo].invoiced += inv.total || 0;
    });
    return map;
  }, [filteredInvoices]);

  // Collected per month from payments (use payment date)
  const collectedByMonth = useMemo(() => {
    const map = {};
    payments.filter(p => p.entity_type === 'client').forEach(p => {
      if (!p.date) return;
      const d = new Date(p.date);
      if (d.getFullYear() !== year) return;
      const mo = d.getMonth();
      map[mo] = (map[mo] || 0) + (p.amount || 0);
    });
    return map;
  }, [payments, year]);

  const monthRows = months.map((name, i) => {
    const inv = byMonth[i]?.invoiced || 0;
    const coll = collectedByMonth[i] || 0;
    const out = inv - coll;
    return [name, `${fmt(inv)} ${cur}`, `${fmt(coll)} ${cur}`, `${fmt(out)} ${cur}`];
  }).filter((_, i) => (byMonth[i]?.invoiced || 0) > 0 || (collectedByMonth[i] || 0) > 0);

  // By client
  const byClient = useMemo(() => {
    const map = {};
    filteredInvoices.forEach(inv => {
      const name = inv.client_name;
      if (!map[name]) map[name] = { count: 0, invoiced: 0 };
      map[name].count += 1;
      map[name].invoiced += inv.total || 0;
    });
    return map;
  }, [filteredInvoices]);

  const clientRows = Object.entries(byClient)
    .sort((a, b) => b[1].invoiced - a[1].invoiced)
    .map(([name, d]) => {
      const paid = paymentsByClient[name] || 0;
      const out = d.invoiced - paid;
      return [name, d.count, `${fmt(d.invoiced)} ${cur}`, `${fmt(paid)} ${cur}`, `${fmt(Math.max(0, out))} ${cur}`];
    });

  const totalInv = filteredInvoices.reduce((s, i) => s + (i.total || 0), 0);
  const totalColl = Object.values(collectedByMonth).reduce((s, v) => s + v, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={t('totalInvoiced')} value={`${fmt(totalInv)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totalColl)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totalInv - totalColl))} ${cur}`} color="text-red-600" language={language} />
      </div>
      <SectionTitle language={language}>{t('revenueReport')} — {year}</SectionTitle>
      {monthRows.length > 0 ? (
        <ReportTable
          head={[language === 'ar' ? 'الشهر' : 'Month', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
          rows={monthRows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', `${fmt(totalInv)} ${cur}`, `${fmt(totalColl)} ${cur}`, `${fmt(Math.max(0, totalInv - totalColl))} ${cur}`]}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل العملاء' : 'By Client'}</SectionTitle>
      {clientRows.length > 0 ? (
        <ReportTable
          head={[t('clients'), '#', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
          rows={clientRows}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 2 — CLIENT BALANCES
// ══════════════════════════════════════════════════════════════════════════════
const ClientBalancesReport = ({ invoices, payments, t, language }) => {
  const cur = t('currency');

  const data = useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      const n = inv.client_name;
      if (!map[n]) map[n] = { count: 0, invoiced: 0 };
      map[n].count++;
      map[n].invoiced += inv.total || 0;
    });
    const paid = {};
    payments.filter(p => p.entity_type === 'client').forEach(p => {
      paid[p.entity_id] = (paid[p.entity_id] || 0) + (p.amount || 0);
    });
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d, paid: paid[name] || 0, balance: d.invoiced - (paid[name] || 0) }))
      .sort((a, b) => b.balance - a.balance);
  }, [invoices, payments]);

  const totalInv  = data.reduce((s, d) => s + d.invoiced, 0);
  const totalPaid = data.reduce((s, d) => s + d.paid, 0);
  const totalBal  = data.reduce((s, d) => s + d.balance, 0);

  const rows = data.map(d => {
    const balColor = d.balance <= 0 ? 'text-green-600' : d.paid > 0 ? 'text-yellow-600' : 'text-red-600';
    return [
      d.name,
      d.count,
      `${fmt(d.invoiced)} ${cur}`,
      `${fmt(d.paid)} ${cur}`,
      <span className={`font-semibold ${balColor}`}>{fmt(d.balance)} {cur}</span>,
    ];
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={t('totalInvoiced')} value={`${fmt(totalInv)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totalPaid)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totalBal))} ${cur}`} color="text-red-600" language={language} />
      </div>
      <ReportTable
        head={[t('clients'), '#', t('totalInvoiced'), t('totalCollected'), t('outstanding')]}
        rows={rows}
        foot={[language === 'ar' ? 'الإجمالي' : 'Total', data.length, `${fmt(totalInv)} ${cur}`, `${fmt(totalPaid)} ${cur}`, `${fmt(Math.max(0, totalBal))} ${cur}`]}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 3 — P&L SUMMARY
// ══════════════════════════════════════════════════════════════════════════════
const LEDGER_EXCLUDE = ['Client Payment', 'Supplier Payment', 'Outsourcing', 'Salaries', 'Withdrawal'];

const PLReport = ({ invoices, payments, employees, outsourcingPayments, ledger, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');
  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  const monthlyPayroll = useMemo(() =>
    employees.reduce((s, e) => s + (e.salary || 0) + (e.ss || 0), 0), [employees]);

  const rows = useMemo(() => {
    return Array.from({ length: 12 }, (_, mo) => {
      const moStr = String(mo + 1).padStart(2, '0');
      const rev = invoices
        .filter(i => i.date && new Date(i.date).getFullYear() === year && new Date(i.date).getMonth() === mo)
        .reduce((s, i) => s + (i.total || 0), 0);
      if (rev === 0) return null;
      const outCost = outsourcingPayments
        .filter(p => p.payment_date && new Date(p.payment_date).getFullYear() === year && new Date(p.payment_date).getMonth() === mo)
        .reduce((s, p) => s + (p.amount || 0), 0);
      const supplPay = payments
        .filter(p => p.entity_type === 'supplier' && p.date && new Date(p.date).getFullYear() === year && new Date(p.date).getMonth() === mo)
        .reduce((s, p) => s + (p.amount || 0), 0);
      const otherExp = ledger
        .filter(e => e.credit > 0 && e.date && new Date(e.date).getFullYear() === year && new Date(e.date).getMonth() === mo && !LEDGER_EXCLUDE.includes(e.transaction_type))
        .reduce((s, e) => s + (e.credit || 0), 0);
      const totalCosts = monthlyPayroll + outCost + supplPay + otherExp;
      const net = rev - totalCosts;
      return { mo, rev, monthlyPayroll, outCost, supplPay, otherExp, totalCosts, net };
    }).filter(Boolean);
  }, [invoices, payments, employees, outsourcingPayments, ledger, year, monthlyPayroll]);

  const totals = rows.reduce((acc, r) => ({
    rev: acc.rev + r.rev,
    payroll: acc.payroll + r.monthlyPayroll,
    out: acc.out + r.outCost,
    supp: acc.supp + r.supplPay,
    other: acc.other + r.otherExp,
    costs: acc.costs + r.totalCosts,
    net: acc.net + r.net,
  }), { rev: 0, payroll: 0, out: 0, supp: 0, other: 0, costs: 0, net: 0 });

  const tableRows = rows.map(r => [
    months[r.mo],
    `${fmt(r.rev)} ${cur}`,
    `${fmt(r.monthlyPayroll)} ${cur}`,
    `${fmt(r.outCost)} ${cur}`,
    `${fmt(r.supplPay)} ${cur}`,
    `${fmt(r.otherExp)} ${cur}`,
    `${fmt(r.totalCosts)} ${cur}`,
    <span className={r.net >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>{fmt(r.net)} {cur}</span>,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('revenue')} value={`${fmt(totals.rev)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('payrollCost')} value={`${fmt(totals.payroll)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={t('outsourcingCost')} value={`${fmt(totals.out)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses'} value={`${fmt(totals.other)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={t('netProfit')} value={`${fmt(totals.net)} ${cur}`} color={totals.net >= 0 ? 'text-green-600' : 'text-red-600'} language={language} />
      </div>
      {tableRows.length > 0 ? (
        <ReportTable
          head={[language === 'ar' ? 'الشهر' : 'Month', t('revenue'), t('payrollCost'), t('outsourcingCost'), t('supplierPayments'), language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses', t('totalCosts'), t('netProfit')]}
          rows={tableRows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', `${fmt(totals.rev)} ${cur}`, `${fmt(totals.payroll)} ${cur}`, `${fmt(totals.out)} ${cur}`, `${fmt(totals.supp)} ${cur}`, `${fmt(totals.other)} ${cur}`, `${fmt(totals.costs)} ${cur}`, `${fmt(totals.net)} ${cur}`]}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 4 — MONTHLY INCOME STATEMENT
// ══════════════════════════════════════════════════════════════════════════════
const IncomeStatement = ({ invoices, payments, employees, outsourcingPayments, ledger, t, language }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const cur = t('currency');

  const data = useMemo(() => {
    const inMonth = (dateStr) => {
      if (!dateStr) return false;
      const d = new Date(dateStr);
      return d.getFullYear() === year && d.getMonth() + 1 === month;
    };
    const monthInvoices = invoices.filter(i => inMonth(i.date));
    const sales     = monthInvoices.reduce((s, i) => s + (i.subtotal || 0), 0);
    const vat       = monthInvoices.reduce((s, i) => s + (i.vat_amount || 0), 0);
    const totalRev  = sales + vat;
    const outCost   = outsourcingPayments.filter(p => inMonth(p.payment_date)).reduce((s, p) => s + (p.amount || 0), 0);
    const grossProfit = totalRev - outCost;
    const payroll   = employees.reduce((s, e) => s + (e.salary || 0) + (e.ss || 0), 0);
    const supplPay  = payments.filter(p => p.entity_type === 'supplier' && inMonth(p.date)).reduce((s, p) => s + (p.amount || 0), 0);
    const otherExp  = ledger
      .filter(e => e.credit > 0 && inMonth(e.date) && !LEDGER_EXCLUDE.includes(e.transaction_type))
      .reduce((s, e) => s + (e.credit || 0), 0);
    const opEx      = payroll + supplPay + otherExp;
    const netIncome = grossProfit - opEx;
    return { sales, vat, totalRev, outCost, grossProfit, payroll, supplPay, otherExp, opEx, netIncome };
  }, [invoices, payments, employees, outsourcingPayments, ledger, month, year]);

  const Row = ({ label, value, indent = false, bold = false, separator = false, color }) => (
    <>
      {separator && <div className="border-t border-border my-1" />}
      <div className={`flex justify-between items-center py-1.5 ${indent ? 'ps-6' : ''}`}>
        <span className={`text-sm ${bold ? 'font-semibold' : 'text-muted-foreground'}`} lang={language === 'ar' ? 'ar' : 'en'}>{label}</span>
        <span className={`text-sm font-mono-numeric ${bold ? 'font-semibold' : ''} ${color || ''}`}>{fmt(value)} {cur}</span>
      </div>
    </>
  );

  const months = language === 'ar' ? MONTHS_AR : MONTHS;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MonthYearFilter month={month} year={year} onMonth={setMonth} onYear={setYear} language={language} />
      </div>
      <Card className="p-6 max-w-xl border border-border">
        <h3 className="text-base font-bold mb-4 text-center" lang={language === 'ar' ? 'ar' : 'en'}>
          {t('incomeStatement')} — {months[month - 1]} {year}
        </h3>
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('revenue')}</p>
          <Row label={language === 'ar' ? 'المبيعات (صافي)' : 'Sales (net)'} value={data.sales} indent />
          <Row label={t('vatCollected')} value={data.vat} indent />
          <Row label={language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'} value={data.totalRev} bold separator color="text-green-700" />

          <div className="mt-3" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('costOfSales')}</p>
          <Row label={t('outsourcingCost')} value={data.outCost} indent />
          <Row label={t('grossProfit')} value={data.grossProfit} bold separator color={data.grossProfit >= 0 ? 'text-green-700' : 'text-red-600'} />

          <div className="mt-3" />
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1" lang={language === 'ar' ? 'ar' : 'en'}>{t('operatingExpenses')}</p>
          <Row label={t('payrollCost')} value={data.payroll} indent />
          <Row label={t('supplierPayments')} value={data.supplPay} indent />
          <Row label={language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses'} value={data.otherExp} indent />
          <Row label={t('operatingExpenses')} value={data.opEx} bold separator />

          <div className="mt-2 border-t-2 border-border pt-2">
            <div className="flex justify-between items-center py-1.5">
              <span className="text-base font-bold" lang={language === 'ar' ? 'ar' : 'en'}>{t('netIncome')}</span>
              <span className={`text-base font-bold font-mono-numeric ${data.netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {fmt(data.netIncome)} {cur}
              </span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 5 — ORDERS REPORT
// ══════════════════════════════════════════════════════════════════════════════
const OrdersReport = ({ orders, t, language }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [itemFilter,   setItemFilter]   = useState('all');

  const clients   = useMemo(() => [...new Set(orders.map(o => o.client_name))].sort(), [orders]);
  const itemCodes = useMemo(() => [...new Set(orders.map(o => o.item_code))].sort(), [orders]);

  const filtered = useMemo(() => orders.filter(o =>
    (statusFilter === 'all' || o.status === statusFilter) &&
    (clientFilter === 'all' || o.client_name === clientFilter) &&
    (itemFilter   === 'all' || o.item_code   === itemFilter)
  ), [orders, statusFilter, clientFilter, itemFilter]);

  const counts = useMemo(() => ({
    total:        orders.length,
    pending:      orders.filter(o => o.status === 'pending').length,
    inProduction: orders.filter(o => o.status === 'in-production').length,
    completed:    orders.filter(o => o.status === 'completed').length,
  }), [orders]);

  const statusBadge = (s) => {
    const map = {
      pending:       'bg-yellow-100 text-yellow-800',
      'in-production':'bg-blue-100 text-blue-800',
      completed:     'bg-green-100 text-green-800',
      cancelled:     'bg-red-100 text-red-800',
    };
    const labels = {
      pending:        t('pending'),
      'in-production': t('inProduction'),
      completed:      t('completed'),
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[s] || 'bg-muted text-muted-foreground'}`} lang={language === 'ar' ? 'ar' : 'en'}>{labels[s] || s}</span>;
  };

  const rows = filtered.map(o => {
    const pieces = (o.variants || []).reduce((s, v) => s + (v.quantity || 0), 0);
    return [
      o.order_number,
      o.client_name,
      o.items,
      pieces.toLocaleString(),
      statusBadge(o.status),
      o.procurement_date || '—',
      o.production_date  || '—',
      o.delivery_date    || '—',
    ];
  });

  const selClass = "border border-border rounded-md px-3 py-1.5 text-sm bg-background";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders'} value={counts.total} language={language} />
        <StatCard label={t('pending')} value={counts.pending} color="text-yellow-600" language={language} />
        <StatCard label={t('inProduction')} value={counts.inProduction} color="text-blue-600" language={language} />
        <StatCard label={t('completed')} value={counts.completed} color="text-green-600" language={language} />
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={selClass}>
          <option value="all">{t('all')}</option>
          <option value="pending">{t('pending')}</option>
          <option value="in-production">{t('inProduction')}</option>
          <option value="completed">{t('completed')}</option>
        </select>
        <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} className={selClass}>
          <option value="all">{language === 'ar' ? 'كل العملاء' : 'All Clients'}</option>
          {clients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={itemFilter} onChange={e => setItemFilter(e.target.value)} className={selClass}>
          <option value="all">{language === 'ar' ? 'كل الأصناف' : 'All Items'}</option>
          {itemCodes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <ReportTable
        head={[t('orderNumber'), t('clients'), language === 'ar' ? 'الصنف' : 'Item', t('totalPieces'), t('status'), language === 'ar' ? 'الشراء' : 'Procurement', language === 'ar' ? 'الإنتاج' : 'Production', language === 'ar' ? 'التسليم' : 'Delivery']}
        rows={rows}
      />
      {rows.length === 0 && <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 6 — DELIVERY PERFORMANCE
// ══════════════════════════════════════════════════════════════════════════════
const DeliveryPerformanceReport = ({ orders, t, language }) => {
  const daysBetween = (a, b) => {
    if (!a || !b) return null;
    return Math.round((new Date(b) - new Date(a)) / 86400000);
  };

  const completed = useMemo(() =>
    orders.filter(o => o.status === 'fulfilled' && o.procurement_date && o.delivery_date)
      .map(o => ({
        ...o,
        procToProd: daysBetween(o.procurement_date, o.production_date),
        prodToDel:  daysBetween(o.production_date, o.delivery_date),
        total:      daysBetween(o.procurement_date, o.delivery_date),
      })), [orders]);

  const avg = (key) => {
    const vals = completed.map(o => o[key]).filter(v => v !== null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  };

  const rows = completed.map(o => [
    o.order_number,
    o.client_name,
    o.items,
    o.procToProd !== null ? o.procToProd : '—',
    o.prodToDel  !== null ? o.prodToDel  : '—',
    <span className="font-semibold">{o.total !== null ? o.total : '—'}</span>,
  ]);

  const avgRow = [
    t('averages'), '', '',
    Math.round(avg('procToProd')),
    Math.round(avg('prodToDel')),
    <span className="font-semibold">{Math.round(avg('total'))}</span>,
  ];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={language === 'ar' ? 'عدد الطلبات المكتملة' : 'Completed Orders'} value={completed.length} language={language} />
        <StatCard label={t('avgCycleTime')} value={`${Math.round(avg('total'))} ${language === 'ar' ? 'يوم' : 'days'}`} language={language} />
        <StatCard label={language === 'ar' ? 'متوسط إنتاج → تسليم' : 'Avg Prod → Delivery'} value={`${Math.round(avg('prodToDel'))} ${language === 'ar' ? 'يوم' : 'days'}`} language={language} />
      </div>
      {rows.length > 0 ? (
        <ReportTable
          head={[t('orderNumber'), t('clients'), language === 'ar' ? 'الصنف' : 'Item', t('procToProduction'), t('prodToDelivery'), t('totalCycle')]}
          rows={rows}
          foot={avgRow}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 7 — PAYROLL SUMMARY
// ══════════════════════════════════════════════════════════════════════════════
const PayrollSummaryReport = ({ employees, t, language }) => {
  const cur = t('currency');

  const byCategory = useMemo(() => {
    const map = {};
    employees.forEach(e => {
      const cat = e.category || 'Other';
      if (!map[cat]) map[cat] = { count: 0, salary: 0, ss: 0 };
      map[cat].count++;
      map[cat].salary += e.salary || 0;
      map[cat].ss     += e.ss     || 0;
    });
    return map;
  }, [employees]);

  const totals = useMemo(() => ({
    salary: employees.reduce((s, e) => s + (e.salary || 0), 0),
    ss:     employees.reduce((s, e) => s + (e.ss || 0), 0),
    net:    employees.reduce((s, e) => s + (e.salary || 0) + (e.ss || 0), 0),
  }), [employees]);

  const catRows = Object.entries(byCategory)
    .sort((a, b) => b[1].salary - a[1].salary)
    .map(([cat, d]) => [
      cat,
      d.count,
      `${fmt(d.salary)} ${cur}`,
      `${fmt(d.ss)} ${cur}`,
      `${fmt(d.salary + d.ss)} ${cur}`,
    ]);

  const empRows = [...employees]
    .sort((a, b) => (b.salary || 0) - (a.salary || 0))
    .map(e => [
      e.name,
      e.employee_number,
      e.category,
      `${fmt(e.salary)} ${cur}`,
      `${fmt(e.ss)} ${cur}`,
      `${fmt((e.salary || 0) + (e.ss || 0))} ${cur}`,
    ]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t('employees')} value={employees.length} language={language} />
        <StatCard label={t('baseSalary')} value={`${fmt(totals.salary)} ${cur}`} language={language} />
        <StatCard label={language === 'ar' ? 'التأمينات الاجتماعية' : 'Social Security'} value={`${fmt(totals.ss)} ${cur}`} language={language} />
        <StatCard label={t('totalNetPay')} value={`${fmt(totals.net)} ${cur}`} color="text-primary" language={language} />
      </div>
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب الفئة' : 'By Category'}</SectionTitle>
      <ReportTable
        head={[t('category'), t('employees'), t('baseSalary'), language === 'ar' ? 'تأمينات' : 'SS', t('totalNetPay')]}
        rows={catRows}
        foot={[language === 'ar' ? 'الإجمالي' : 'Total', employees.length, `${fmt(totals.salary)} ${cur}`, `${fmt(totals.ss)} ${cur}`, `${fmt(totals.net)} ${cur}`]}
      />
      <SectionTitle language={language}>{language === 'ar' ? 'جميع الموظفين' : 'All Employees'}</SectionTitle>
      <ReportTable
        head={[t('name'), language === 'ar' ? 'الرقم الوظيفي' : 'Emp #', t('category'), t('salary'), language === 'ar' ? 'تأمينات' : 'SS', t('netPay')]}
        rows={empRows}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT 8 — OUTSOURCING COST REPORT
// ══════════════════════════════════════════════════════════════════════════════
const OutsourcingCostReport = ({ workers, jobs, outsourcingPayments, t, language }) => {
  const cur = t('currency');

  const workerMap = useMemo(() => {
    const m = {};
    workers.forEach(w => { m[w.id] = w.name; });
    return m;
  }, [workers]);

  const byWorker = useMemo(() => {
    const m = {};
    workers.forEach(w => { m[w.id] = { name: w.name, jobs: 0, completed: 0, work: 0 }; });
    jobs.forEach(j => {
      if (!m[j.worker_id]) return;
      m[j.worker_id].jobs++;
      if (j.status === 'completed') m[j.worker_id].completed++;
      m[j.worker_id].work += j.total_cost || 0;
    });
    outsourcingPayments.forEach(p => {
      if (!m[p.worker_id]) m[p.worker_id] = { name: p.worker_name, jobs: 0, completed: 0, work: 0, paid: 0 };
      if (!m[p.worker_id].paid) m[p.worker_id].paid = 0;
      m[p.worker_id].paid += p.amount || 0;
    });
    return Object.entries(m).map(([id, d]) => ({
      ...d,
      paid:    d.paid || 0,
      balance: (d.work || 0) - (d.paid || 0),
    }));
  }, [workers, jobs, outsourcingPayments]);

  const byItem = useMemo(() => {
    const m = {};
    jobs.forEach(j => {
      const code = j.item_code || '—';
      if (!m[code]) m[code] = { jobs: 0, qty: 0, cost: 0 };
      m[code].jobs++;
      m[code].qty  += j.quantity   || 0;
      m[code].cost += j.total_cost || 0;
    });
    return Object.entries(m).sort((a, b) => b[1].cost - a[1].cost);
  }, [jobs]);

  const totals = useMemo(() => ({
    jobs:    jobs.length,
    work:    byWorker.reduce((s, d) => s + d.work,    0),
    paid:    byWorker.reduce((s, d) => s + d.paid,    0),
    balance: byWorker.reduce((s, d) => s + d.balance, 0),
  }), [jobs, byWorker]);

  const workerRows = byWorker.map(d => [
    d.name,
    d.jobs,
    d.completed,
    `${fmt(d.work)} ${cur}`,
    `${fmt(d.paid)} ${cur}`,
    <span className={`font-semibold ${d.balance <= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(d.balance)} {cur}</span>,
  ]);

  const itemRows = byItem.map(([code, d]) => [
    code,
    d.jobs,
    d.qty.toLocaleString(),
    `${fmt(d.cost)} ${cur}`,
    d.jobs > 0 ? `${fmt(d.cost / d.jobs)} ${cur}` : '—',
  ]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={language === 'ar' ? 'إجمالي الأعمال' : 'Total Jobs'} value={totals.jobs} language={language} />
        <StatCard label={language === 'ar' ? 'إجمالي قيمة العمل' : 'Total Work Value'} value={`${fmt(totals.work)} ${cur}`} language={language} />
        <StatCard label={t('totalCollected')} value={`${fmt(totals.paid)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={t('outstanding')} value={`${fmt(Math.max(0, totals.balance))} ${cur}`} color={totals.balance > 0 ? 'text-red-600' : 'text-green-600'} language={language} />
      </div>
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب العامل' : 'By Worker'}</SectionTitle>
      <ReportTable
        head={[t('name'), language === 'ar' ? 'الأعمال' : 'Jobs', language === 'ar' ? 'مكتمل' : 'Done', language === 'ar' ? 'قيمة العمل' : 'Work Value', t('totalCollected'), t('outstanding')]}
        rows={workerRows}
      />
      <SectionTitle language={language}>{language === 'ar' ? 'تفصيل حسب كود الصنف' : 'By Item Code'}</SectionTitle>
      <ReportTable
        head={[t('itemCode'), language === 'ar' ? 'الأعمال' : 'Jobs', t('quantity'), language === 'ar' ? 'إجمالي التكلفة' : 'Total Cost', language === 'ar' ? 'متوسط التكلفة/عمل' : 'Avg Cost/Job']}
        rows={itemRows}
      />
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT — VAT REPORT
// ══════════════════════════════════════════════════════════════════════════════
const VATReport = ({ invoices, supplierInvoices, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');
  const isAr = language === 'ar';

  const clientInv = useMemo(() =>
    invoices.filter(i => i.date && new Date(i.date).getFullYear() === year)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [invoices, year]
  );

  const suppInv = useMemo(() =>
    supplierInvoices.filter(i => i.date && new Date(i.date).getFullYear() === year)
      .sort((a, b) => new Date(a.date) - new Date(b.date)),
    [supplierInvoices, year]
  );

  const clientTotals = useMemo(() => ({
    net: clientInv.reduce((s, i) => s + (i.subtotal || 0), 0),
    vat: clientInv.reduce((s, i) => s + (i.vat_amount || 0), 0),
  }), [clientInv]);

  const suppTotals = useMemo(() => ({
    net: suppInv.reduce((s, i) => s + (i.subtotal || 0), 0),
    vat: suppInv.reduce((s, i) => s + (i.vat_amount || 0), 0),
  }), [suppInv]);

  const netVAT = clientTotals.vat - suppTotals.vat;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label={isAr ? 'ضريبة المبيعات (العملاء)' : 'VAT Collected (Clients)'} value={`${fmt(clientTotals.vat)} ${cur}`} color="text-green-600" language={language} />
        <StatCard label={isAr ? 'ضريبة المشتريات (الموردين)' : 'VAT Paid (Suppliers)'} value={`${fmt(suppTotals.vat)} ${cur}`} color="text-red-600" language={language} />
        <StatCard label={isAr ? 'صافي الضريبة المستحقة' : 'Net VAT Payable'} value={`${fmt(netVAT)} ${cur}`} color={netVAT >= 0 ? 'text-orange-600' : 'text-green-600'} language={language} />
      </div>

      {/* Side-by-side tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Client invoices */}
        <div>
          <SectionTitle language={language}>{isAr ? 'فواتير العملاء' : 'Client Invoices'}</SectionTitle>
          {clientInv.length > 0 ? (
            <ReportTable
              head={[isAr ? 'التاريخ' : 'Date', isAr ? 'العميل' : 'Client', isAr ? 'الإجمالي بدون ضريبة' : 'Net Amount', isAr ? 'الضريبة' : 'VAT']}
              rows={clientInv.map(i => [
                i.date,
                i.client_name,
                `${fmt(i.subtotal || 0)} ${cur}`,
                `${fmt(i.vat_amount || 0)} ${cur}`,
              ])}
              foot={[isAr ? 'الإجمالي' : 'Total', '', `${fmt(clientTotals.net)} ${cur}`, `${fmt(clientTotals.vat)} ${cur}`]}
            />
          ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
        </div>

        {/* Supplier invoices */}
        <div>
          <SectionTitle language={language}>{isAr ? 'فواتير الموردين' : 'Supplier Invoices'}</SectionTitle>
          {suppInv.length > 0 ? (
            <ReportTable
              head={[isAr ? 'التاريخ' : 'Date', isAr ? 'المورد' : 'Supplier', isAr ? 'الإجمالي بدون ضريبة' : 'Net Amount', isAr ? 'الضريبة' : 'VAT']}
              rows={suppInv.map(i => [
                i.date,
                i.supplier_name,
                `${fmt(i.subtotal || 0)} ${cur}`,
                `${fmt(i.vat_amount || 0)} ${cur}`,
              ])}
              foot={[isAr ? 'الإجمالي' : 'Total', '', `${fmt(suppTotals.net)} ${cur}`, `${fmt(suppTotals.vat)} ${cur}`]}
            />
          ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// REPORT — OTHER EXPENSES (cash book detail)
// ══════════════════════════════════════════════════════════════════════════════
const OtherExpensesReport = ({ ledger, t, language }) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const cur = t('currency');

  const filtered = useMemo(() =>
    ledger.filter(e =>
      e.credit > 0 &&
      e.date && new Date(e.date).getFullYear() === year &&
      !LEDGER_EXCLUDE.includes(e.transaction_type)
    ).sort((a, b) => new Date(a.date) - new Date(b.date)),
    [ledger, year]
  );

  const total = filtered.reduce((s, e) => s + (e.credit || 0), 0);

  const byType = filtered.reduce((acc, e) => {
    acc[e.transaction_type] = (acc[e.transaction_type] || 0) + (e.credit || 0);
    return acc;
  }, {});

  const rows = filtered.map(e => [
    e.date,
    e.transaction_type,
    e.description || '—',
    `${fmt(e.credit)} ${cur}`,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <YearFilter value={year} onChange={setYear} t={t} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(byType).sort((a, b) => b[1] - a[1]).map(([type, amt]) => (
          <StatCard key={type} label={type} value={`${fmt(amt)} ${cur}`} color="text-red-600" language={language} />
        ))}
        <StatCard label={language === 'ar' ? 'الإجمالي' : 'Total'} value={`${fmt(total)} ${cur}`} color="text-red-700" language={language} />
      </div>
      {rows.length > 0 ? (
        <ReportTable
          head={[
            language === 'ar' ? 'التاريخ' : 'Date',
            language === 'ar' ? 'النوع' : 'Type',
            language === 'ar' ? 'البيان' : 'Description',
            language === 'ar' ? 'المبلغ' : 'Amount',
          ]}
          rows={rows}
          foot={[language === 'ar' ? 'الإجمالي' : 'Total', '', '', `${fmt(total)} ${cur}`]}
        />
      ) : <p className="text-sm text-muted-foreground py-4">{t('noDataForPeriod')}</p>}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN REPORTS PAGE
// ══════════════════════════════════════════════════════════════════════════════
export const Reports = () => {
  const { getAuthHeader } = useAuth();
  const { t, language }   = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'revenue';

  const [loading, setLoading]                   = useState(true);
  const [invoices, setInvoices]                 = useState([]);
  const [payments, setPayments]                 = useState([]);
  const [employees, setEmployees]               = useState([]);
  const [orders, setOrders]                     = useState([]);
  const [workers, setWorkers]                   = useState([]);
  const [jobs, setJobs]                         = useState([]);
  const [outsourcingPayments, setOutPay]        = useState([]);
  const [ledger, setLedger]                     = useState([]);
  const [supplierInvoices, setSupplierInvoices] = useState([]);

  useEffect(() => {
    const headers = getAuthHeader();
    Promise.all([
      axios.get(`${API}/invoices`,              { headers }),
      axios.get(`${API}/payments`,              { headers }),
      axios.get(`${API}/employees`,             { headers }),
      axios.get(`${API}/orders`,                { headers }),
      axios.get(`${API}/outsourcing/workers`,   { headers }),
      axios.get(`${API}/outsourcing/jobs`,      { headers }),
      axios.get(`${API}/outsourcing/payments`,  { headers }),
      axios.get(`${API}/ledger`,                { headers }),
      axios.get(`${API}/supplier-invoices`,     { headers }),
    ]).then(([inv, pay, emp, ord, wrk, jbs, opp, led, sinv]) => {
      setInvoices(inv.data             || []);
      setPayments(pay.data             || []);
      setEmployees(emp.data            || []);
      setOrders(ord.data               || []);
      setWorkers(wrk.data              || []);
      setJobs(jbs.data                 || []);
      setOutPay(opp.data               || []);
      setLedger(led.data               || []);
      setSupplierInvoices(sinv.data    || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px] text-muted-foreground">
        <span lang={language === 'ar' ? 'ar' : 'en'}>{t('loading')}</span>
      </div>
    );
  }

  const commonProps = { t, language };

  return (
    <div className="p-8 max-w-7xl">
      <h1 className="text-4xl font-bold tracking-tight mb-8" lang={language === 'ar' ? 'ar' : 'en'}>
        {t('reports')}
      </h1>

      <Tabs value={tab} onValueChange={v => setSearchParams({ tab: v })}>
        <TabsList className="mb-6 flex-wrap h-auto gap-1">
          <TabsTrigger value="revenue"          lang={language === 'ar' ? 'ar' : 'en'}>{t('revenueReport')}</TabsTrigger>
          <TabsTrigger value="client-balances"  lang={language === 'ar' ? 'ar' : 'en'}>{t('clientBalances')}</TabsTrigger>
          <TabsTrigger value="pl"               lang={language === 'ar' ? 'ar' : 'en'}>{t('plSummary')}</TabsTrigger>
          <TabsTrigger value="income-statement" lang={language === 'ar' ? 'ar' : 'en'}>{t('incomeStatement')}</TabsTrigger>
          <TabsTrigger value="orders"           lang={language === 'ar' ? 'ar' : 'en'}>{t('ordersReport')}</TabsTrigger>
          <TabsTrigger value="delivery"         lang={language === 'ar' ? 'ar' : 'en'}>{t('deliveryPerformance')}</TabsTrigger>
          <TabsTrigger value="payroll"          lang={language === 'ar' ? 'ar' : 'en'}>{t('payrollSummary')}</TabsTrigger>
          <TabsTrigger value="outsourcing"      lang={language === 'ar' ? 'ar' : 'en'}>{t('outsourcingReport')}</TabsTrigger>
          <TabsTrigger value="other-expenses"   lang={language === 'ar' ? 'ar' : 'en'}>{language === 'ar' ? 'مصاريف أخرى' : 'Other Expenses'}</TabsTrigger>
          <TabsTrigger value="vat"              lang={language === 'ar' ? 'ar' : 'en'}>{language === 'ar' ? 'تقرير الضريبة' : 'VAT Report'}</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue">
          <RevenueReport invoices={invoices} payments={payments} {...commonProps} />
        </TabsContent>

        <TabsContent value="client-balances">
          <ClientBalancesReport invoices={invoices} payments={payments} {...commonProps} />
        </TabsContent>

        <TabsContent value="pl">
          <PLReport invoices={invoices} payments={payments} employees={employees} outsourcingPayments={outsourcingPayments} ledger={ledger} {...commonProps} />
        </TabsContent>

        <TabsContent value="income-statement">
          <IncomeStatement invoices={invoices} payments={payments} employees={employees} outsourcingPayments={outsourcingPayments} ledger={ledger} {...commonProps} />
        </TabsContent>

        <TabsContent value="orders">
          <OrdersReport orders={orders} {...commonProps} />
        </TabsContent>

        <TabsContent value="delivery">
          <DeliveryPerformanceReport orders={orders} {...commonProps} />
        </TabsContent>

        <TabsContent value="payroll">
          <PayrollSummaryReport employees={employees} {...commonProps} />
        </TabsContent>

        <TabsContent value="outsourcing">
          <OutsourcingCostReport workers={workers} jobs={jobs} outsourcingPayments={outsourcingPayments} {...commonProps} />
        </TabsContent>

        <TabsContent value="other-expenses">
          <OtherExpensesReport ledger={ledger} {...commonProps} />
        </TabsContent>

        <TabsContent value="vat">
          <VATReport invoices={invoices} supplierInvoices={supplierInvoices} {...commonProps} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
